import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { utilisationState } from '../utils/utilisation';

export type ZagProgressRingTone = 'brand' | 'success' | 'warning' | 'error';

/** Circular progress ring with a projected centre; `max <= 0` renders an empty track without arc. */
@Component({
  selector: 'zag-progress-ring',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'zag-progress-ring',
    '[class.zag-progress-ring--success]': 'tone() === "success"',
    '[class.zag-progress-ring--warning]': 'tone() === "warning"',
    '[class.zag-progress-ring--error]': 'tone() === "error"',
    '[class.zag-progress-ring--warn]': 'state() === "warn"',
    '[class.zag-progress-ring--over]': 'state() === "over"',
    '[style.width.px]': 'size()',
    '[style.height.px]': 'size()',
    '[attr.role]': 'hasTarget() ? "meter" : "img"',
    '[attr.aria-label]': 'ariaLabel()',
    '[attr.aria-valuetext]': 'hasTarget() ? valueText() ?? null : null',
    '[attr.aria-valuemin]': 'hasTarget() ? 0 : null',
    '[attr.aria-valuemax]': 'hasTarget() ? max() : null',
    '[attr.aria-valuenow]': 'hasTarget() ? clampedValue() : null',
  },
  template: `
    <svg class="zag-progress-ring__svg" viewBox="0 0 120 120" aria-hidden="true" focusable="false">
      <circle class="zag-progress-ring__track" cx="60" cy="60" r="52" stroke-width="10" />
      <circle
        class="zag-progress-ring__arc"
        [class.zag-progress-ring__arc--empty]="ratio() === 0"
        cx="60"
        cy="60"
        r="52"
        stroke-width="10"
        [attr.pathLength]="pathLength"
        [attr.stroke-dasharray]="pathLength"
        [style.stroke-dashoffset]="dashOffset()"
      />
    </svg>
    <div class="zag-progress-ring__content"><ng-content /></div>
  `,
  styleUrl: './progress-ring.component.scss',
})
export class ZagProgressRing {
  readonly value = input.required<number>();
  /** Value at which the ring is full; `<= 0` means there is no target. */
  readonly max = input.required<number>();
  /** Accessible name stating the numbers; the projected centre is presentational for assistive tech. */
  readonly ariaLabel = input.required<string>();
  /** Human-readable value announced instead of the computed percentage. */
  readonly valueText = input<string>();
  readonly tone = input<ZagProgressRingTone>('brand');
  /** Edge length in px. */
  readonly size = input<number>(132);

  protected readonly pathLength = 100;

  protected readonly hasTarget = computed(() => this.max() > 0);
  protected readonly clampedValue = computed(() => Math.min(Math.max(this.value(), 0), this.max()));
  protected readonly ratio = computed(() => (this.hasTarget() ? this.clampedValue() / this.max() : 0));
  protected readonly state = computed(() => (this.hasTarget() ? utilisationState(this.value(), this.max()) : 'ok'));
  protected readonly dashOffset = computed(() => this.pathLength * (1 - this.ratio()));
}
